<template>
  <section class="trust-section" ref="root">
    <!-- Encabezado -->
    <header class="trust-head" ref="head">
      <h2 class="trust-eyebrow">Limpieza</h2>
      <h1 class="trust-title">
        <span class="t-blue">Nuestros</span>
        <span class="t-gold"> Servicios</span>
      </h1>
      <p class="trust-sub">
        En EA Magic Cleaning LLC, creemos que cada cliente merece un ambiente limpio, saludable y acogedor.
      </p>
    </header>

    <!-- Bloques numerados -->
    <div class="trust-list">
      <article
        v-for="(item, i) in items"
        :key="item.title || i"
        class="trust-item"
        :class="{ 'is-reverse': i % 2 === 1 }"
        :ref="el => rowEls[i] = el"
      >
        <!-- Imagen -->
        <figure class="trust-media">
          <img :src="item.img" :alt="item.title" loading="lazy" />
        </figure>

        <!-- Texto -->
        <div class="trust-copy">
          <div class="trust-badge">{{ i + 1 }}</div>
          <h3 class="trust-h">{{ item.title }}</h3>
          <p class="trust-p">
            <span v-if="item.highlight" class="trust-strong">{{ item.highlight }}</span>
            {{ item.text }}
          </p>
          <!-- 🔗 Botón con enlace específico por servicio -->
          <a class="trust-cta" :href="item.wa || wa" target="_blank" rel="noopener">Agenda tu limpieza</a>
        </div>
      </article>
    </div>
  </section>
</template>
<script>
// ✅ Import estático (funciona en Vite/Vue CLI). Asegúrate que los archivos existan en /src/assets
import imgCasas     from '@/assets/casas.jpg'
import imgDepa      from '@/assets/depa.png'
import imgConst     from '@/assets/const.jpg'
import imgEntrada   from '@/assets/entrada.jpg'
import imgProfunda  from '@/assets/profunda.webp'
import imgRegular   from '@/assets/regular.webp'
import imgIglesias  from '@/assets/iglesias_limpieza.jpg'
import imgOficinas  from '@/assets/oficinas.jpg'

import { nextTick } from 'vue'
import { gsap } from 'gsap'

export default {
  name: 'SeccionConfiable',

  data() {
    return {
      // Fallback general (se usará solo si algún item no trae su wa)
      wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza de casas',

      // ⬇️ Servicios con imágenes + enlace SMS específico en cada uno
      items: [
        {
          emoji: '🏡',
          title: 'Limpieza de Casas',
          highlight: 'Cuidado completo para tu hogar ',
          text: 'incluyendo salas, baños, cocinas y recámaras. Nuestro equipo se encarga de cada rincón para que disfrutes de un espacio fresco, higiénico y acogedor.',
          img: imgCasas,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza de casas'
        },
        {
          emoji: '🏢',
          title: 'Limpieza de apartamentos',
          highlight: 'Orden y frescura en espacios compactos ',
          text: 'con enfoque en superficies de alto contacto, cocina y baño.',
          img: imgDepa,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza de apartamentos'
        },
        {
          emoji: '🛠',
          title: 'Limpieza post-construcción',
          highlight: 'Removemos polvo fino y residuos ',
          text: 'para entregar un espacio impecable y listo para habitar.',
          img: imgConst,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza post-construcción'
        },
        {
          emoji: '🚚',
          title: 'Limpieza en Mudanzas',
          highlight: 'Mudanzas sin estrés ',
          text: 'con limpieza profunda para entregar o recibir el lugar en perfectas condiciones.',
          img: imgEntrada,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza de entrada/salida'
        },
        {
          emoji: '✨',
          title: 'Limpieza profunda',
          highlight: 'Enfocados en zonas difíciles ',
          text: 'como detrás de muebles, juntas y detalles olvidados para un brillo total.',
          img: imgProfunda,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza profunda'
        },
        {
          emoji: '🔄',
          title: 'Limpieza regular',
          highlight: 'Planes semanales o quincenales ',
          text: 'para mantener tu espacio siempre limpio sin preocuparte por nada.',
          img: imgRegular,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza regular'
        },
        {
          emoji: '⛪',
          title: 'Limpieza de iglesias',
          highlight: 'Respeto y pulcritud en espacios de culto ',
          text: 'con protocolos adecuados para áreas comunes y santuarios.',
          img: imgIglesias,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza de iglesias'
        },
        {
          emoji: '💼',
          title: 'Limpieza de oficinas',
          highlight: 'Ambientes de trabajo limpios y saludables ',
          text: 'con atención a escritorios, salas de juntas y áreas comunes.',
          img: imgOficinas,
          wa: 'sms:+16122619287?&body=Hola, me interesa información sobre Limpieza de oficina'
        }
      ],

      // 🔹 refs para animación
      rowEls: [],
      ctx: null,
      ioHead: null,
      ioRows: null
    }
  },

  async mounted() {
    await nextTick()
    const root = this.$refs.root
    const head = this.$refs.head
    const rows = this.rowEls.filter(Boolean)

    // Estado inicial con GSAP (y contexto para revertir limpio)
    this.ctx = gsap.context(() => {
      gsap.set(head, { autoAlpha: 0, y: 24 })
      gsap.set(rows, { autoAlpha: 0, y: 28 })
      gsap.set(rows.map(r => r.querySelector('.trust-media img')), { scale: 1.06 })
    }, root)

    // Observer para el header
    this.ioHead = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return
        gsap.to(head, { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out' })
        this.ioHead && this.ioHead.disconnect()
      })
    }, { threshold: 0.25 })
    this.ioHead.observe(head)

    // Observer para cada fila (tarjeta)
    this.ioRows = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return
        const el = e.target

        // Elementos internos de la tarjeta
        const img     = el.querySelector('.trust-media img')
        const badge   = el.querySelector('.trust-badge')
        const title   = el.querySelector('.trust-h')
        const para    = el.querySelector('.trust-p')
        const cta     = el.querySelector('.trust-cta')

        const tl = gsap.timeline({ defaults: { ease: 'power2.out' } })
        tl.to(el, { autoAlpha: 1, y: 0, duration: 0.5 })
          .to(img, { scale: 1, duration: 0.6 }, '<')
          .from([badge, title, para, cta], {
            y: 14, autoAlpha: 0, duration: 0.45, stagger: 0.08
          }, '-=0.25')

        this.ioRows.unobserve(el) // no repetir animación
      })
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 })

    rows.forEach(r => this.ioRows.observe(r))
  },

  beforeUnmount() {
    if (this.ioHead) this.ioHead.disconnect()
    if (this.ioRows) this.ioRows.disconnect()
    if (this.ctx) this.ctx.revert()
  }
}
</script>


<style scoped>
/* Paleta y tipografía */
:root {
  --blue: #2a62ff;
  --gold: #f4c44d;
  --ink:  #121e33;
  --muted:#55627a;
  --bg:   #ffffff;
}

.trust-section {
  background: var(--bg);
  padding: 36px 0 48px;
  font-family: 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
}

/* Head */
.trust-head {
  max-width: 1100px;
  margin: 0 auto 12px;
  padding: 0 24px;
  text-align: left;
}
.trust-eyebrow {
  margin: 0;
  font-size: 20px;
  color: var(--muted);
  font-weight: 600;
  letter-spacing: .3px;
}
.trust-title {
  margin: 4px 0 8px;
  font-size: clamp(32px, 6vw, 56px);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: .5px;
}
.t-blue { color: #13213a; }
.t-gold { color: #d5a21b; }
.trust-sub {
  margin: 0 0 22px;
  color: #445068;
  font-size: 16px;
}

/* Lista */
.trust-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  gap: 30px;
}

/* Item */
.trust-item {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 28px;
  align-items: center;
  background: #f6f7f9;
  border-radius: 18px;
  overflow: clip;
  box-shadow: 0 10px 26px rgba(0,0,0,.06);
}
.trust-item.is-reverse { grid-template-columns: 1fr 1.1fr; }
.trust-item.is-reverse .trust-media { order: 2; }
.trust-item.is-reverse .trust-copy  { order: 1; }

/* Imagen */
.trust-media {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
}
.trust-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.025);
  transition: transform .4s ease;
}
.trust-item:hover .trust-media img { transform: scale(1.06); }

/* Texto */
.trust-copy {
  padding: 22px 22px 22px 0;
  position: relative;
}
.trust-item.is-reverse .trust-copy { padding: 22px 0 22px 22px; }

.trust-badge {
  width: 74px; height: 74px;
  border-radius: 999px;
  background: #5ab44c;
  color: #fff;
  font-weight: 800;
  font-size: 28px;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 18px rgba(90,180,76,.35);
  margin-bottom: 14px;
}

.trust-h {
  margin: 0 0 8px;
  font-size: clamp(22px, 3.5vw, 36px);
  line-height: 1.15;
  color: #2c3e66;
  letter-spacing: .2px;
}

.trust-p {
  margin: 0 0 14px;
  color: #2a3550;
  font-size: 16px;
  line-height: 1.7;
}
.trust-strong {
  font-weight: 800;
  color: #192441;
}

/* CTA */
.trust-cta {
  display: inline-block;
  background: var(--gold);
  color: #0e1a2f;
  font-weight: 800;
  font-size: 14px;
  padding: 12px 16px;
  border-radius: 12px;
  text-decoration: none;
  box-shadow: 0 8px 18px rgba(0,0,0,.15);
  transition: transform .2s ease, box-shadow .2s ease;
}
.trust-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(0,0,0,.22); }

/* Responsive */
@media (max-width: 980px) {
  .trust-item,
  .trust-item.is-reverse {
    grid-template-columns: 1fr;
  }
  .trust-media,
  .trust-item.is-reverse .trust-media { order: 1; }
  .trust-copy,
  .trust-item.is-reverse .trust-copy { order: 2; padding: 16px 18px 22px; }
}
</style>

<style scoped>
/* Paleta y tipografía */
:root {
  --blue: #fbff2a;
  --gold: #f4c44d;
  --ink:  #121e33;
  --muted:#55627a;
  --bg:   #ffffff;
}

.trust-section {
  background: var(--bg);
  padding: 36px 0 48px;
  font-family: 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji';
}

/* Head */
.trust-head {
  max-width: 1100px;
  margin: 0 auto 12px;
  padding: 0 24px;
  text-align: left;
}
.trust-eyebrow {
  margin: 0;
  font-size: 20px;
  color: var(--muted);
  font-weight: 600;
  letter-spacing: .3px;
}
.trust-title {
  margin: 4px 0 8px;
  font-size: clamp(32px, 6vw, 56px);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: .5px;
}
.t-blue { color:  #13213a; }
.t-gold { color: #c7d13a; }
.trust-sub {
  margin: 0 0 22px;
  color: #686844;
  font-size: 16px;
}

/* Lista */
.trust-list {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  gap: 30px;
}

/* Item */
.trust-item {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 28px;
  align-items: center;
  background: #f6f7f9;
  border-radius: 18px;
  overflow: clip;
  box-shadow: 0 10px 26px rgba(0,0,0,.06);
}
.trust-item.is-reverse { grid-template-columns: 1fr 1.1fr; }
.trust-item.is-reverse .trust-media { order: 2; }
.trust-item.is-reverse .trust-copy  { order: 1; }

/* Imagen */
.trust-media {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
}
.trust-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.025);
  transition: transform .4s ease;
}
.trust-item:hover .trust-media img { transform: scale(1.06); }

/* Texto */
.trust-copy {
  padding: 22px 22px 22px 0;
  position: relative;
}
.trust-item.is-reverse .trust-copy { padding: 22px 0 22px 22px; }

.trust-badge {
  width: 74px; height: 74px;
  border-radius: 999px;
  background: #5ab44c;
  color: #fff;
  font-weight: 800;
  font-size: 28px;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 18px rgba(90,180,76,.35);
  margin-bottom: 14px;
}

.trust-h {
  margin: 0 0 8px;
  font-size: clamp(22px, 3.5vw, 36px);
  line-height: 1.15;
  color: #bfc744;
  letter-spacing: .2px;
}

.trust-p {
  margin: 0 0 14px;
  color: #2a3550;
  font-size: 16px;
  line-height: 1.7;
}
.trust-strong {
  font-weight: 800;
  color: #192441;
}

/* CTA */
.trust-cta {
  display: inline-block;
  background: var(--gold);
  color: #0e1a2f;
  font-weight: 800;
  font-size: 14px;
  padding: 12px 16px;
  border-radius: 12px;
  text-decoration: none;
  box-shadow: 0 8px 18px rgba(0,0,0,.15);
  transition: transform .2s ease, box-shadow .2s ease;
}
.trust-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(0,0,0,.22); }

/* Responsive */
@media (max-width: 980px) {
  .trust-item,
  .trust-item.is-reverse {
    grid-template-columns: 1fr;
  }
  .trust-media,
  .trust-item.is-reverse .trust-media { order: 1; }
  .trust-copy,
  .trust-item.is-reverse .trust-copy { order: 2; padding: 16px 18px 22px; }
}
</style>
